import React from 'react';
import {
    ITEM_HEIGHT,
    LOOP_BLOCKS,
    SCROLL_IDLE_MS,
    SNAP_DURATION_MS,
} from './constants';
import {
    easeOutCubic,
    getCenteredRawIndex,
    getCenteredWindowRange,
    wrapValue,
} from './utils';

type TimeColumnItem = {
    rawIndex: number;
    value: number;
};

interface UseTimeColumnScrollArgs {
    value: number;
    total: number;
    onChange: (value: number) => void;
}

interface UseTimeColumnScrollResult {
    viewportRef: React.RefObject<HTMLDivElement | null>;
    handleViewportRef: (node: HTMLDivElement | null) => void;
    items: TimeColumnItem[];
    topSpacerHeight: number;
    bottomSpacerHeight: number;
    handleScroll: () => void;
    handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
    handleInteractionStart: () => void;
    handleItemSelect: (rawIndex: number) => void;
    focusViewport: () => void;
}

export function useTimeColumnScroll({
    value,
    total,
    onChange,
}: UseTimeColumnScrollArgs): UseTimeColumnScrollResult {
    const viewportRef = React.useRef<HTMLDivElement | null>(null);
    const currentRawIndexRef = React.useRef<number | null>(null);
    const lastValueRef = React.useRef(value);
    const idleTimeoutRef = React.useRef<number | null>(null);
    const snapFrameRef = React.useRef<number | null>(null);
    const isSnappingRef = React.useRef(false);
    const initialScrollTop = getCenteredRawIndex(value, total) * ITEM_HEIGHT;
    const [scrollTop, setScrollTop] = React.useState(initialScrollTop);

    const totalItems = total * LOOP_BLOCKS;
    const maxRawIndex = totalItems - 1;
    const currentWindowCenterIndex = Math.max(
        0,
        Math.min(maxRawIndex, Math.round(scrollTop / ITEM_HEIGHT))
    );
    const { start: windowStart, end: windowEnd } = getCenteredWindowRange(
        currentWindowCenterIndex,
        maxRawIndex
    );
    const topSpacerHeight = windowStart * ITEM_HEIGHT;
    const bottomSpacerHeight = Math.max(
        0,
        (maxRawIndex - windowEnd) * ITEM_HEIGHT
    );
    const items = Array.from(
        { length: windowEnd - windowStart + 1 },
        (_, index) => {
            const rawIndex = windowStart + index;

            return {
                rawIndex,
                value: wrapValue(rawIndex, total),
            };
        }
    );

    const clearIdleTimeout = React.useCallback(() => {
        if (idleTimeoutRef.current !== null) {
            window.clearTimeout(idleTimeoutRef.current);
            idleTimeoutRef.current = null;
        }
    }, []);

    const handleViewportRef = React.useCallback(
        (node: HTMLDivElement | null) => {
            viewportRef.current = node;

            if (!node || currentRawIndexRef.current !== null) {
                return;
            }

            node.scrollTop = initialScrollTop;
            currentRawIndexRef.current = Math.round(
                initialScrollTop / ITEM_HEIGHT
            );
        },
        [initialScrollTop]
    );

    const cancelSnapAnimation = React.useCallback(() => {
        if (snapFrameRef.current !== null) {
            window.cancelAnimationFrame(snapFrameRef.current);
            snapFrameRef.current = null;
        }

        isSnappingRef.current = false;
    }, []);

    const scrollToRawIndex = React.useCallback(
        (rawIndex: number) => {
            const viewport = viewportRef.current;
            if (!viewport) {
                return;
            }

            const clampedRawIndex = Math.max(
                0,
                Math.min(maxRawIndex, rawIndex)
            );
            const nextScrollTop = clampedRawIndex * ITEM_HEIGHT;

            currentRawIndexRef.current = clampedRawIndex;
            setScrollTop(nextScrollTop);
            viewport.scrollTop = nextScrollTop;
        },
        [maxRawIndex]
    );

    const syncValueForRawIndex = React.useCallback(
        (rawIndex: number) => {
            const nextValue = wrapValue(rawIndex, total);

            if (nextValue !== lastValueRef.current) {
                lastValueRef.current = nextValue;
                onChange(nextValue);
            }
        },
        [onChange, total]
    );

    const snapToRawIndex = React.useCallback(
        (rawIndex: number) => {
            const viewport = viewportRef.current;
            if (!viewport) {
                return;
            }

            clearIdleTimeout();
            cancelSnapAnimation();

            const clampedRawIndex = Math.max(
                0,
                Math.min(maxRawIndex, rawIndex)
            );
            const startTop = viewport.scrollTop;
            const targetTop = clampedRawIndex * ITEM_HEIGHT;

            if (Math.abs(startTop - targetTop) < 1) {
                currentRawIndexRef.current = clampedRawIndex;
                setScrollTop(targetTop);
                viewport.scrollTop = targetTop;
                return;
            }

            const startTime = performance.now();
            isSnappingRef.current = true;

            const animate = (timestamp: number) => {
                const progress = Math.min(
                    1,
                    (timestamp - startTime) / SNAP_DURATION_MS
                );
                const easedProgress = easeOutCubic(progress);

                viewport.scrollTop =
                    startTop + (targetTop - startTop) * easedProgress;

                if (progress < 1 && isSnappingRef.current) {
                    snapFrameRef.current =
                        window.requestAnimationFrame(animate);
                    return;
                }

                snapFrameRef.current = null;
                viewport.scrollTop = targetTop;
                currentRawIndexRef.current = clampedRawIndex;
                setScrollTop(targetTop);

                window.requestAnimationFrame(() => {
                    isSnappingRef.current = false;
                });
            };

            snapFrameRef.current = window.requestAnimationFrame(animate);
        },
        [cancelSnapAnimation, clearIdleTimeout, maxRawIndex]
    );

    const scheduleIdleSnap = React.useCallback(() => {
        clearIdleTimeout();

        idleTimeoutRef.current = window.setTimeout(() => {
            const viewport = viewportRef.current;
            if (!viewport) {
                idleTimeoutRef.current = null;
                return;
            }

            idleTimeoutRef.current = null;
            snapToRawIndex(Math.round(viewport.scrollTop / ITEM_HEIGHT));
        }, SCROLL_IDLE_MS);
    }, [clearIdleTimeout, snapToRawIndex]);

    React.useEffect(() => {
        lastValueRef.current = value;
    }, [value]);

    React.useEffect(() => {
        const currentRawIndex = currentRawIndexRef.current;

        if (
            currentRawIndex !== null &&
            wrapValue(currentRawIndex, total) === value
        ) {
            return;
        }

        scrollToRawIndex(getCenteredRawIndex(value, total));
    }, [scrollToRawIndex, total, value]);

    const handleScroll = React.useCallback(() => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }

        const nextScrollTop = viewport.scrollTop;
        const rawIndex = Math.max(
            0,
            Math.min(maxRawIndex, Math.round(nextScrollTop / ITEM_HEIGHT))
        );

        setScrollTop(nextScrollTop);
        currentRawIndexRef.current = rawIndex;
        syncValueForRawIndex(rawIndex);

        if (!isSnappingRef.current) {
            scheduleIdleSnap();
        }
    }, [maxRawIndex, scheduleIdleSnap, syncValueForRawIndex]);

    const selectRawIndex = React.useCallback(
        (rawIndex: number) => {
            clearIdleTimeout();
            cancelSnapAnimation();
            scrollToRawIndex(rawIndex);
            syncValueForRawIndex(rawIndex);
        },
        [
            cancelSnapAnimation,
            clearIdleTimeout,
            scrollToRawIndex,
            syncValueForRawIndex,
        ]
    );

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
                return;
            }

            event.preventDefault();

            const delta = event.key === 'ArrowDown' ? 1 : -1;
            const fallbackRawIndex = getCenteredRawIndex(value, total);
            const nextRawIndex =
                (currentRawIndexRef.current ?? fallbackRawIndex) + delta;

            selectRawIndex(nextRawIndex);
        },
        [selectRawIndex, total, value]
    );

    const handleInteractionStart = React.useCallback(() => {
        clearIdleTimeout();
        cancelSnapAnimation();
    }, [cancelSnapAnimation, clearIdleTimeout]);

    const focusViewport = React.useCallback(() => {
        viewportRef.current?.focus();
    }, []);

    React.useEffect(
        () => () => {
            clearIdleTimeout();
            cancelSnapAnimation();
        },
        [cancelSnapAnimation, clearIdleTimeout]
    );

    return {
        viewportRef,
        handleViewportRef,
        items,
        topSpacerHeight,
        bottomSpacerHeight,
        handleScroll,
        handleKeyDown,
        handleInteractionStart,
        handleItemSelect: selectRawIndex,
        focusViewport,
    };
}
